/**
 * Positions — components.md §3's four rules, and the one that costs the most.
 *
 * > **Rows persist through resolution.** A position does not vanish at expiry;
 * > it sits in `RESOLVING` with `Mark`/`Value` blanked to `—` (there is no book
 * > to mark against) and its PnL undetermined. **Blank is the honest render, not
 * > a stale last price.**
 *
 * That is the whole of `markable()` below. A market past `OPEN` has no book —
 * §7.6 drops the ladder and `ladderAt` returns empty — so any mark still on
 * screen is a price from a market that has stopped trading, sitting next to a
 * P&L computed from it. It reads as live and it is not.
 *
 * **Both assets live in one table.** A user holds BTC and ETH at the same anchor
 * routinely; splitting them would double the scanning cost for no information
 * gain. Sorted by anchor descending, never re-sorted.
 *
 * The `position_value` and `unrealized_pnl` fields are **forecasts and outside
 * the balance identity** (invariant 9) — they are rendered here, in a table of
 * forecasts, and never summed into the balance chip.
 */

import { memo, useMemo } from 'react'
import { EmptyState, Glyph, Pill, Table } from '@/components/primitives'
import type { TableColumn } from '@/components/primitives'
import { cx } from '@/lib/cx'
import { formatAvgCents, formatCents, formatShares, formatSignedUsdc, formatUsdc } from '@/lib/format'
import { usePositions } from '@/app/use-runtime'
import { isMarketId, parseMarketId } from '@/lib/market-id'

type Row = Record<string, unknown>

/** States with a live book to mark against. Everything else blanks. */
const MARKABLE = new Set(['OPEN'])

export const PositionsTable = memo(function PositionsTable({ className }: { className?: string }) {
  const { rows } = usePositions<Row>()

  const sorted = useMemo(
    () =>
      [...rows]
        .filter((p) => hasShares(p))
        // Anchor descending, then market id — a stable total order, so a row
        // never moves under a pointer (components.md §3, rule 1).
        .sort((a, b) => {
          const byAnchor = anchorOfRow(b) - anchorOfRow(a)
          return byAnchor !== 0 ? byAnchor : String(a.market_id).localeCompare(String(b.market_id))
        }),
    [rows],
  )

  const columns = useMemo<TableColumn<Row>[]>(
    () => [
      {
        id: 'market',
        header: 'Market',
        card: 'title',
        cell: (row) => (
          <span className="truncate text-body-sm" title={String(row.market_id)}>
            {String(row.market_id)}
          </span>
        ),
      },
      {
        id: 'outcome',
        header: 'Outcome',
        card: 'title',
        cell: (row) => {
          const { up, down } = heldLegs(row)
          if (up === null && down === null) return '—'
          // **Both legs, when both are held.** A pair is riskless and pays
          // once, and naming only the net side would hide the other half of
          // what the account actually owns — the shares are on chain either
          // way. Neither leg is the direction of the position, so neither
          // colour is honest: this is the one row that renders neutral.
          if (up !== null && down !== null) {
            return (
              <span className="flex items-center gap-1 text-text-secondary">
                <Glyph name="up" label={null} />
                <Glyph name="down" label={null} />
                BOTH
              </span>
            )
          }
          const outcome = up !== null ? 'UP' : 'DOWN'
          return (
            <span className={cx('flex items-center gap-1', outcome === 'UP' ? 'text-up-text' : 'text-down-text')}>
              <Glyph name={outcome === 'UP' ? 'up' : 'down'} label={null} />
              {outcome}
            </span>
          )
        },
      },
      {
        id: 'size',
        header: 'Size',
        numeric: true,
        // `up_shares` / `down_shares` are the authoritative fields; `net_shares`
        // is a derived convenience "nothing in the payout path reads"
        // (`openapi.Position`) — and it is `0.000000` for a complete set, which
        // is exactly why it is not read here. The size shown is the one that
        // settles: both legs when both are held, because the payout is
        // `max(up, down)` and a single number cannot say which.
        cell: (row) => {
          const { up, down } = heldLegs(row)
          if (up === null && down === null) return '—'
          if (up !== null && down !== null) return `${formatShares(up)} / ${formatShares(down)}`
          return formatShares((up ?? down) as string)
        },
      },
      {
        id: 'avg',
        header: 'Avg',
        numeric: true,
        cell: (row) =>
          typeof row.avg_entry_price_cents === 'string' ? formatAvgCents(row.avg_entry_price_cents) : '—',
      },
      {
        id: 'mark',
        header: 'Mark',
        numeric: true,
        cell: (row) => (markable(row) && typeof row.mark_price_cents === 'number' ? formatCents(row.mark_price_cents) : '—'),
      },
      {
        id: 'value',
        header: 'Value',
        numeric: true,
        cell: (row) => (markable(row) && typeof row.position_value === 'string' ? formatUsdc(row.position_value) : '—'),
      },
      {
        id: 'pnl',
        header: 'PnL',
        numeric: true,
        cell: (row) => {
          if (!markable(row) || typeof row.unrealized_pnl !== 'string') return '—'
          const negative = row.unrealized_pnl.trimStart().startsWith('-')
          return (
            <span className={negative ? 'text-down-text' : 'text-up-text'}>
              {formatSignedUsdc(row.unrealized_pnl)}
            </span>
          )
        },
      },
      {
        id: 'state',
        header: 'State',
        card: 'badge',
        cell: (row) => <Pill tone={markable(row) ? 'up' : 'neutral'}>{String(row.settlement_status ?? row.state ?? '—')}</Pill>,
      },
    ],
    [],
  )

  return (
    <Table
      label="positions"
      {...(className ? { className } : {})}
      density="comfortable"
      columns={columns}
      rows={sorted}
      rowKey={(row) => String(row.market_id)}
      empty={<EmptyState>No open positions.</EmptyState>}
    />
  )
})

// ---------------------------------------------------------------------------

/**
 * The legs actually held — each one's share count, or `null` where there is
 * none.
 *
 * **It reads the two share counts and never `outcome`, and that is the whole
 * fix (§F55).** `Position.outcome` is `UP | DOWN | FLAT`, widened by §F16
 * because "a user who bought UP and later bought DOWN in the same market can
 * hold a position that nets to zero". This file used to take `FLAT` as "no
 * position" and filter the row out — true only while §8.3(h)'s
 * `POSITION_MERGE` collapsed every overlap on the fill that created it, and
 * that netting is off wherever the venue settles on chain, because nothing can
 * submit `mergePositions` on an account's behalf. So a real 100 UP + 100 DOWN
 * holding rendered as no row at all: the user saw an empty table while the CTF
 * held both legs and the dollar behind them.
 *
 * `openapi.Position` is explicit about which fields to trust — "`up_shares` and
 * `down_shares` are the authoritative fields", and `net_shares` is "a derived
 * convenience field... nothing in the payout path reads it", which for a
 * complete set is `0.000000`.
 */
function heldLegs(row: Row): { up: string | null; down: string | null } {
  const up = String(row.up_shares ?? '0')
  const down = String(row.down_shares ?? '0')
  return { up: nonZero(up) ? up : null, down: nonZero(down) ? down : null }
}

/** A decimal string with any digit other than zero in it. */
function nonZero(shares: string): boolean {
  return /[1-9]/.test(shares)
}

function hasShares(row: Row): boolean {
  const { up, down } = heldLegs(row)
  return up !== null || down !== null
}

/**
 * Is there a book to mark this against?
 *
 * Reads the market state carried on the position rather than looking one up: a
 * position for a market this tab is not subscribed to still has to render
 * honestly, and the `positions` frame carries the state for exactly that.
 */
function markable(row: Row): boolean {
  const state = row.market_state ?? row.state
  return typeof state === 'string' ? MARKABLE.has(state) : true
}

function anchorOfRow(row: Row): number {
  const id = row.market_id
  return typeof id === 'string' && isMarketId(id) ? parseMarketId(id).anchorS : 0
}
